import axios, { Axios, AxiosRequestConfig, AxiosResponse } from "axios";
import qs from "qs";
import { IActionResponse, IActionResult } from "./actionResult";
import { ObjectHelper, StringHelper, TokenHelper } from "../../../assets/utils/helpers";
import { ISnackbarConfiguration, SnackbarConfiguration } from "../../configuration";
import { IApplicationSettings, GetApplicationSetting } from "../../../assets/configuration";

type ApiErrorValue = string | string[];

type ApiErrorResponseData = {
    operationStatusCode?: number;
    status?: number;
    isSuccess?: boolean;
    errorMessages?: string[];
    errors?: Record<string, ApiErrorValue>;
    detail?: string;
    title?: string;
    [key: string]: unknown;
};

type ApiError = {
    response?: {
        data?: ApiErrorResponseData;
        status?: number;
    };
    message?: string;
};

export interface IApiClient {
    checkAuth(): void;
    setSnackbarConfiguration(snackbarConfiguration: ISnackbarConfiguration): void;

    getAsync<T, U = IActionResult<T>>(routeUrl: string, params?: Record<string, any>, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, signal?: AbortSignal): Promise<U>;
    postAsync<T, U = T, V = IActionResult<U>>(routeUrl: string, body?: T, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): Promise<V>
    putAsync<T, U = T, V = IActionResult<U>>(routeUrl: string, body: any, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): Promise<V>
    deleteAsync<T = IActionResponse>(routeUrl: string, params: Record<string, any>, customErrorFunc?: (error: any) => any, signal?: AbortSignal): Promise<T>;
}
export class ApiClient implements IApiClient {
    private snackbarConfiguration: ISnackbarConfiguration;

    constructor(snackbarConfiguration?: ISnackbarConfiguration | undefined) {
        const settings: IApplicationSettings = GetApplicationSetting;
        this.snackbarConfiguration = snackbarConfiguration !== undefined && snackbarConfiguration !== null ? snackbarConfiguration : new SnackbarConfiguration();
    }


    // Public methods
    checkAuth() {
        const token: any | undefined = TokenHelper.getAccessToken();

        return new Promise((resolve, reject) => {
            if (token) {
                this._setAuthTokenHeader(token);
                resolve(true);
            } else {
                this._deleteAuthTokenHeader();
                //reject(false);
            }
        });
    }

    setSnackbarConfiguration(snackbarConfiguration: ISnackbarConfiguration) {
        if (ObjectHelper.isNotNullOrUndefined(snackbarConfiguration)) {
            this.snackbarConfiguration = snackbarConfiguration;
        }
    }

    // Private Methods
    _setAuthTokenHeader(authToken: any) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    }

    _deleteAuthTokenHeader() {
        delete axios.defaults.headers.common["Authorization"];
    }

    private async _makeCallAsync<T>(config: AxiosRequestConfig, customErrorFunc?: (error: any) => any): Promise<T> {
        const call = () => {
            this.checkAuth();

            return axios(config)
                .then((response: AxiosResponse<T>) => response.data)
                .catch((error: ApiError) => {
                    if (axios.isCancel(error)) {
                        return this.normalizeCancelledResponse<T>();
                    }
                    if (customErrorFunc) {
                        throw customErrorFunc(error);
                    }
                    return this.normalizeErrorResponse<T>(error);
                });
        };

        return await call();
    }

    private normalizeCancelledResponse<T>(): T {
        return {
            operationStatusCode: 0,
            isSuccess: false,
            isCancelled: true,
            errorMessages: [],
        } as T;
    }

    private normalizeErrorResponse<T>(error: ApiError): T {
        const responseData = error.response?.data;
        const status = error.response?.status;

        if (responseData) {
            const errorMessages = this.extractErrorMessages(responseData);
            return {
                ...responseData,
                operationStatusCode: responseData.operationStatusCode ?? responseData.status ?? status,
                isSuccess: false,
                errorMessages: responseData.errorMessages ?? errorMessages,
            } as T;
        }

        return {
            operationStatusCode: status,
            isSuccess: false,
            errorMessages: [error.message ?? "Error, unable to process your request please try again later."],
        } as T;
    }

    private extractErrorMessages(responseData: ApiErrorResponseData): string[] {
        if (responseData.errorMessages) {
            return responseData.errorMessages;
        }

        if (responseData.errors) {
            return Object.values(responseData.errors)
                .flatMap((value: ApiErrorValue) => Array.isArray(value) ? value : [value])
                .filter((value: string): boolean => value.length > 0);
        }

        if (responseData.detail) {
            return [responseData.detail];
        }

        if (responseData.title) {
            return [responseData.title];
        }

        return [];
    }

    async getAsync<T, U = IActionResult<T>>(routeUrl: string, params?: Record<string, any>, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, signal?: AbortSignal): Promise<U> {
        const config: AxiosRequestConfig = this.configureGetConfig(routeUrl, params, headers, signal);
        return await this._makeCallAsync<U>(config, customErrorFunc);
    }

    private configureGetConfig(routeUrl: string, params?: Record<string, any>, headers?: Record<string, any>, signal?: AbortSignal): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            method: "GET",
            url: routeUrl,
            params,
            paramsSerializer: (params: any) =>
                qs.stringify(params, { arrayFormat: "repeat" }),
            headers,
            signal,
        };

        return config;
    }

    async postAsync<T, U = T, V = IActionResult<U>>(routeUrl: string, body?: T | undefined, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): Promise<V> {
        const config: AxiosRequestConfig = this.configurePostConfig(routeUrl, body, headers, responseType, signal);
        return await this._makeCallAsync<V>(config, customErrorFunc);
    }

    private configurePostConfig<T>(routeUrl: string, body?: T | undefined, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            method: "POST",
            url: routeUrl,
            data: body,
            headers,
            responseType,
            signal,
        };

        return config;
    }

    async putAsync<T, U = T, V = IActionResult<U>>(routeUrl: string, body?: T | undefined, customErrorFunc?: (error: any) => any, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): Promise<V> {
        const config: AxiosRequestConfig = this.configurePutConfig(routeUrl, body, headers, responseType, signal);
        return await this._makeCallAsync<V>(config, customErrorFunc);
    }

    private configurePutConfig<T>(routeUrl: string, body?: T | undefined, headers?: Record<string, any>, responseType?: AxiosRequestConfig["responseType"], signal?: AbortSignal): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            method: "PUT", 
            url: routeUrl,
            data: body,
            headers,
            responseType,
            signal,
        };

        return config;
    }

    async deleteAsync<T = IActionResponse>(routeUrl: string, params: Record<string, any> = {}, customErrorFunc?: (error: any) => any, signal?: AbortSignal): Promise<T> {
        const config: AxiosRequestConfig = this.configureDeleteConfig(routeUrl, params, signal);
        return await this._makeCallAsync<T>(config, customErrorFunc);
    }

    private configureDeleteConfig(routeUrl: string, params?: Record<string, any>, signal?: AbortSignal): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            method: "DELETE",
            url: routeUrl,
            params,
            paramsSerializer: (params: any) =>
                qs.stringify(params, { arrayFormat: "repeat" }),
            signal,
        };

        return config;
    }
}
